// PWA-aware authentication utilities

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

/**
 * Check if the app is running as an installed PWA (standalone mode)
 */
export function isStandalonePWA() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://') ||
    window.matchMedia('(display-mode: fullscreen)').matches
  );
}

/**
 * Initiate Google OAuth
 * Handles both browser and PWA standalone mode
 */
export function initiateGoogleAuth(redirectPath = '/dashboard') {
  if (!GOOGLE_CLIENT_ID) {
    console.error('Google Client ID not configured');
    return;
  }
  
  const baseUrl = window.location.origin;
  const redirectUri = `${baseUrl}/auth/google/callback`;
  
  // Google OAuth URL
  const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  googleAuthUrl.searchParams.set('client_id', GOOGLE_CLIENT_ID);
  googleAuthUrl.searchParams.set('redirect_uri', redirectUri);
  googleAuthUrl.searchParams.set('response_type', 'code');
  googleAuthUrl.searchParams.set('scope', 'openid email profile');
  googleAuthUrl.searchParams.set('access_type', 'offline');
  googleAuthUrl.searchParams.set('prompt', 'consent');
  googleAuthUrl.searchParams.set('state', redirectPath);
  
  // Store the intended redirect path
  sessionStorage.setItem('oauth_redirect_path', redirectPath);
  localStorage.setItem('oauth_redirect_path', redirectPath); // Also in localStorage for PWA
  
  const authUrlString = googleAuthUrl.toString();
  
  // In standalone PWA mode, we need special handling
  // The redirect from Google will go back to the browser, not the PWA
  // But on Android, if the manifest has handle_links: preferred, it should re-open the PWA
  if (isStandalonePWA()) {
    console.log('[PWA Auth] Running in standalone mode, using direct redirect');
    // Store a flag to indicate we're doing PWA auth
    localStorage.setItem('pwa_auth_pending', 'true');
  }
  
  // Direct redirect - this works in most cases
  // For Android PWAs with proper manifest config, the callback will re-open in the PWA
  window.location.href = authUrlString;
}

/**
 * Get the intended redirect path after OAuth
 */
export function getOAuthRedirectPath() {
  // Check both sessionStorage and localStorage (for PWA recovery)
  let redirect = sessionStorage.getItem('oauth_redirect_path');
  if (!redirect) {
    redirect = localStorage.getItem('oauth_redirect_path');
  }
  
  // Clean up
  sessionStorage.removeItem('oauth_redirect_path');
  localStorage.removeItem('oauth_redirect_path');
  localStorage.removeItem('pwa_auth_pending');
  
  // Always return /dashboard if empty, null, or root path
  if (!redirect || redirect === '/' || redirect === '') {
    return '/dashboard';
  }
  
  return redirect;
}

/**
 * Check if there's a pending PWA auth that needs recovery
 */
export function hasPendingPWAAuth() {
  return localStorage.getItem('pwa_auth_pending') === 'true';
}
